// Module: PickupOverviewScreen
// Role: S06 PICKUP substep overview — renders 3 substep cards (done/active/pending)
//       derived from child checkpoints filtered by parent_checkpoint_id.
// Uses: SubstepCard atom, flow-types (Checkpoint, CheckpointAttempt), existing atoms
// Used by: FlowScreen.tsx (case 'PICKUP')

import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { SubstepCard, type SubstepState } from '../atoms/SubstepCard';
import { flowStepLabel } from '../_labels';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

function deriveSubstepState(
  substep: Checkpoint,
  attempts: CheckpointAttempt[],
  isFirstNonDone: boolean,
): SubstepState {
  const hasAccepted = attempts.some(
    (a) => a.checkpoint_id === substep.id && a.verdict === 'accepted',
  );
  if (hasAccepted) return 'done';
  if (isFirstNonDone) return 'active';
  return 'pending';
}

export function PickupOverviewScreen({
  checkpoint,
  checkpoints,
  attempts,
  onCta,
}: TemplateProps) {
  const substeps = checkpoints
    .filter((c) => c.parent_checkpoint_id === checkpoint.id)
    .sort((a, b) => a.position - b.position);

  const parentCount = checkpoints.filter((c) => c.parent_checkpoint_id === null).length;

  if (substeps.length === 0) {
    return (
      <Frame>
        <MiniHeader title={flowStepLabel(checkpoint.kind)} />
        <div className="px-4 pt-3 pb-2">
          <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
          <HintBar>Подшаги не настроены</HintBar>
        </div>
      </Frame>
    );
  }

  let foundFirstNonDone = false;
  const substepStates: SubstepState[] = substeps.map((sub) => {
    const isDone = attempts.some(
      (a) => a.checkpoint_id === sub.id && a.verdict === 'accepted',
    );
    if (isDone) return 'done';
    const isFirst = !foundFirstNonDone;
    foundFirstNonDone = true;
    return deriveSubstepState(sub, attempts, isFirst);
  });

  const activeIndex = substepStates.indexOf('active');
  const activeSubstep = activeIndex >= 0 ? substeps[activeIndex] : null;
  const ctaLabel = activeSubstep?.instructions_text ?? 'Продолжить';

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <InstructionCard>
          <div className="flex flex-col gap-3">
            {substeps.map((sub, i) => (
              <SubstepCard
                key={sub.id}
                index={sub.position}
                parentIndex={checkpoint.position}
                label={sub.instructions_text ?? sub.slug}
                state={substepStates[i]}
                onClick={substepStates[i] === 'active' ? onCta : undefined}
              />
            ))}
          </div>
        </InstructionCard>
      </div>
      <WizardCtaBar label={ctaLabel} onClick={onCta} />
    </Frame>
  );
}
